import { useMemo, useRef } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { ActivityIndicator, Divider, List, Text, TouchableRipple } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppBottomSheet, BottomSheetScrollView } from '@/components/atoms';
import { useUserPickerScrollToSelection } from '@/components/molecules/person-in-charge/useUserPickerScrollToSelection';
import {
  buildUserPickerRows,
  USER_PICKER_ROW_HEIGHT,
  type UserOption,
} from '@/components/molecules/person-in-charge/userPickerListRows';
import {
  userPickerOpenSnapIndex,
  userPickerSelectedRowIndex,
  userPickerViewportHeight,
} from '@/components/molecules/person-in-charge/userPickerScrollToSelection';
import { rippleOnSurface, unisonColors, useAppTheme } from '@/theme';

export type { UserOption };

const PICKER_SNAP_POINTS = ['50%', '90%'] as const;

interface UserPickerSheetProps {
  readonly visible: boolean;
  readonly title: string;
  readonly clearLabel?: string;
  readonly currentUserId: number | null | undefined;
  readonly users: readonly UserOption[];
  readonly loading: boolean;
  readonly showIcons?: boolean;
  readonly onSelect: (userId: number | null) => void;
  readonly onDismiss: () => void;
}

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  scrollView: {
    flex: 1,
  },
  row: {
    justifyContent: 'center',
    minHeight: USER_PICKER_ROW_HEIGHT,
    width: '100%',
  },
  touchable: {
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    width: '100%',
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: USER_PICKER_ROW_HEIGHT,
    paddingHorizontal: 16,
  },
  title: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
  },
  selectedTouchable: {
    backgroundColor: unisonColors.brandGreenWeak,
  },
});

interface SelectedCheckIconProps {
  readonly testID: string;
}

const SelectedCheckIcon = ({ testID }: SelectedCheckIconProps) => (
  <View testID={testID}>
    <List.Icon icon="check" color={unisonColors.colorIconBrandStrong} />
  </View>
);

interface LeftIconProps {
  readonly icon: string;
  readonly testID: string;
}

const LeftIcon = ({ icon, testID }: LeftIconProps) => (
  <View testID={testID}>
    <List.Icon icon={icon} />
  </View>
);

interface SheetItemProps {
  readonly title: string;
  readonly selected: boolean;
  readonly showDivider: boolean;
  readonly showIcon: boolean;
  readonly icon: string;
  readonly onPress: () => void;
  readonly onLayout?: (y: number) => void;
  readonly testID: string;
  readonly selectedIconTestID: string;
}

const SheetItem = ({
  title,
  selected,
  showDivider,
  showIcon,
  icon,
  onPress,
  onLayout,
  testID,
  selectedIconTestID,
}: SheetItemProps) => {
  const { colors } = useAppTheme();

  return (
    <View
      style={styles.row}
      onLayout={
        onLayout
          ? (event) => {
              onLayout(event.nativeEvent.layout.y);
            }
          : undefined
      }>
      <TouchableRipple
        accessibilityLabel={title}
        accessibilityRole="button"
        onPress={onPress}
        rippleColor={rippleOnSurface}
        style={[styles.touchable, selected ? styles.selectedTouchable : undefined]}
        testID={testID}>
        <View style={styles.content} testID={`${testID}-content`}>
          {showIcon ? <LeftIcon icon={icon} testID={`${testID}-left-icon`} /> : null}
          <Text numberOfLines={1} style={[styles.title, { color: colors.onSurface }]}>
            {title}
          </Text>
          {selected ? <SelectedCheckIcon testID={selectedIconTestID} /> : null}
        </View>
      </TouchableRipple>
      {showDivider ? <Divider /> : null}
    </View>
  );
};

const useLatchedOpenSnapIndex = (visible: boolean, desiredSnapIndex: number) => {
  const latchedSnapIndexRef = useRef(desiredSnapIndex);
  const wasVisibleRef = useRef(false);

  if (visible && !wasVisibleRef.current) {
    latchedSnapIndexRef.current = desiredSnapIndex;
  }
  wasVisibleRef.current = visible;

  return latchedSnapIndexRef.current;
};

export const UserPickerSheet = ({
  visible,
  title,
  clearLabel,
  currentUserId,
  users,
  loading,
  showIcons = true,
  onSelect,
  onDismiss,
}: UserPickerSheetProps) => {
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const isNoneSelected = currentUserId == null;
  const hasClearOption = clearLabel != null && clearLabel.length > 0;
  const rows = useMemo(() => buildUserPickerRows(users, clearLabel), [clearLabel, users]);
  const selectedRowIndex = userPickerSelectedRowIndex(currentUserId, users, hasClearOption);
  const openSnapIndex = useLatchedOpenSnapIndex(
    visible,
    userPickerOpenSnapIndex(selectedRowIndex, PICKER_SNAP_POINTS.length),
  );
  const viewportHeight = userPickerViewportHeight(windowHeight, PICKER_SNAP_POINTS[openSnapIndex]);
  const { scrollRef, scrollToSelected, handleSelectedRowLayout, handleSheetChange } =
    useUserPickerScrollToSelection(visible, selectedRowIndex, viewportHeight);

  const rowElements = useMemo(
    () =>
      rows.map((item, index) => {
        const isClear = item.kind === 'clear';

        return (
          <SheetItem
            key={item.key}
            title={item.label}
            selected={isClear ? isNoneSelected : item.id === currentUserId}
            showDivider={index < rows.length - 1}
            showIcon={showIcons}
            icon={isClear ? 'account-off-outline' : 'account-outline'}
            onPress={() => onSelect(isClear ? null : item.id)}
            onLayout={selectedRowIndex === index ? handleSelectedRowLayout : undefined}
            selectedIconTestID={`user-picker-sheet-selected-icon-${item.key}`}
            testID={`user-picker-sheet-item-${item.key}`}
          />
        );
      }),
    [
      currentUserId,
      handleSelectedRowLayout,
      isNoneSelected,
      onSelect,
      rows,
      selectedRowIndex,
      showIcons,
    ],
  );

  return (
    <AppBottomSheet
      visible={visible}
      snapPoints={[...PICKER_SNAP_POINTS]}
      index={openSnapIndex}
      onDismiss={onDismiss}
      onChange={handleSheetChange}>
      <View style={styles.header}>
        <Text variant="titleLarge">{title}</Text>
      </View>
      <Divider />
      {loading ? (
        <ActivityIndicator size="small" style={{ padding: 32 }} />
      ) : (
        <BottomSheetScrollView
          ref={scrollRef}
          testID="user-picker-sheet-scroll"
          style={styles.scrollView}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
          onContentSizeChange={scrollToSelected}>
          {rowElements}
        </BottomSheetScrollView>
      )}
    </AppBottomSheet>
  );
};
